'use client'

import { m, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { ArrowUpRight, Check, Layers, MapPin } from 'lucide-react'
import { useRef, useState } from 'react'

// --- Data ---
const cards = [
  {
    id: 'region',
    number: '01',
    title: 'Работаем по всей Адыгее',
    subtitle: 'География услуг',
    description:
      'Оказываем полный спектр digital-услуг для бизнеса в любом уголке республики, учитывая локальную специфику.',
    features: ['Майкоп', 'Адыгейск', 'Тахтамукай', 'Яблоновский'],
    footer: 'Цифровой горизонт Адыгея',
    icon: MapPin,
    gradient: 'from-blue-600 via-cyan-500 to-teal-400',
    accentRgb: '34, 211, 238', // cyan-400
  },
  {
    id: 'projects',
    number: '02',
    title: 'Типы проектов',
    subtitle: 'Компетенции',
    description:
      'Разрабатываем решения любой сложности: от быстрых лендингов до высоконагруженных корпоративных систем.',
    features: ['Лендинги и промо', 'Интернет-магазины', 'Корпоративные порталы', 'Веб-сервисы'],
    footer: 'Цифровой горизонт Адыгея',
    icon: Layers,
    gradient: 'from-purple-600 via-pink-500 to-rose-400',
    accentRgb: '244, 114, 182', // pink-400
  },
]

function Card({ card, index }: { card: (typeof cards)[0]; index: number }) {
  const [isHovered, setIsHovered] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const mouseXSpring = useSpring(x)
  const mouseYSpring = useSpring(y)

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ['8deg', '-8deg'])
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ['-8deg', '8deg'])

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const width = rect.width
    const height = rect.height
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top
    const xPct = mouseX / width - 0.5
    const yPct = mouseY / height - 0.5
    x.set(xPct)
    y.set(yPct)
  }

  const handleMouseLeave = () => {
    x.set(0)
    y.set(0)
    setIsHovered(false)
  }

  return (
    <m.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
        // @ts-expect-error CSS variable for gradient accent is consumed in class-based shadow utility.
        ['--accent-rgb']: card.accentRgb,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className="group perspective-1000 relative h-full"
    >
      <div
        className={`relative h-full overflow-hidden rounded-[20px] p-5 [box-shadow:0_0_0px_transparent] transition-all duration-500 group-hover:[box-shadow:0_28px_84px_rgb(var(--accent-rgb)_/_0.18)] sm:rounded-[24px] sm:p-6 lg:p-10`}
      >
        {/* Premium glass background */}
        <div className="absolute inset-0 rounded-[24px] bg-[linear-gradient(165deg,rgba(255,255,255,0.085)_0%,rgba(255,255,255,0.03)_52%,rgba(255,255,255,0.012)_100%)] backdrop-blur-xl" />
        <div className="absolute inset-0 rounded-[24px] bg-[radial-gradient(circle_at_0%_0%,rgba(255,255,255,0.14),transparent_42%)]" />

        {/* Border Glow */}
        <div
          className={`absolute inset-0 rounded-[24px] border transition-colors duration-500 ${isHovered ? 'border-white/26' : 'border-white/12'}`}
        />

        {/* Ambient Gradient Blob */}
        <div
          className={`absolute -inset-[100px] bg-gradient-to-br ${card.gradient} rounded-full blur-[120px] transition-opacity duration-700 ${isHovered ? 'opacity-18' : 'opacity-10'}`}
        />

        <div className="relative z-10 flex h-full flex-col">
          {/* Header */}
          <div className="mb-5 flex items-start justify-between sm:mb-8">
            <div
              className={`h-12 w-12 rounded-xl bg-gradient-to-br sm:h-16 sm:w-16 sm:rounded-2xl ${card.gradient} p-[2px] transition-transform duration-500 ${isHovered ? 'scale-110 rotate-3' : ''}`}
            >
              <div className="flex h-full w-full items-center justify-center rounded-xl bg-[#0b1428]/75 backdrop-blur-xl sm:rounded-2xl">
                <card.icon className="h-6 w-6 text-white sm:h-8 sm:w-8" strokeWidth={1.5} />
              </div>
            </div>
            <span
              className={`bg-gradient-to-br text-4xl font-bold sm:text-6xl ${card.gradient} bg-clip-text text-transparent opacity-30 select-none`}
            >
              {card.number}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1">
            <p className="home-caption mb-2 text-gray-400 sm:mb-3">{card.subtitle}</p>
            <h3 className="home-card-title-premium mb-3 text-balance text-white sm:mb-5">
              {card.title}
            </h3>
            <p className="home-lead mb-5 text-gray-300 sm:mb-8">{card.description}</p>

            <div className="mb-5 grid grid-cols-2 gap-2 sm:mb-8 sm:gap-3">
              {card.features.map((feature, i) => (
                <div
                  key={i}
                  className="home-subnote flex items-center gap-2 text-gray-200 sm:gap-3"
                >
                  <div
                    className={`flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br ${card.gradient} opacity-80`}
                  >
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </div>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer / CTA-like element */}
          <div className="mt-auto flex items-center justify-between border-t border-white/10 pt-6">
            <span className="home-meta text-gray-400 transition-colors duration-300 group-hover:text-white">
              {card.footer || 'Цифровой горизонт Адыгея'}
            </span>
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-500 ${
                isHovered
                  ? `bg-gradient-to-br ${card.gradient}`
                  : 'border border-white/15 bg-white/7'
              }`}
            >
              <ArrowUpRight
                className={`h-5 w-5 transition-all duration-300 ${
                  isHovered ? 'translate-x-0.5 -translate-y-0.5 text-white' : 'text-white/70'
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </m.div>
  )
}

interface MaykopRegionBlockProps {
  info?: {
    regionTitle?: string | null
    regionSubtitle?: string | null
    regionText?: string | null
    regionList?: Array<string | null> | null
    regionFooter?: string | null
    competenciesTitle?: string | null
    competenciesSubtitle?: string | null
    competenciesText?: string | null
    competenciesList?: Array<string | null> | null
    competenciesFooter?: string | null
  }
}

export default function MaykopRegionBlock({ info }: MaykopRegionBlockProps) {
  // Default fallback
  const defaultCards = cards

  // If info exists, construct dynamic cards
  const dynamicCards = info
    ? [
        {
          id: 'region',
          number: '01',
          title: info.regionTitle || defaultCards[0].title,
          subtitle: info.regionSubtitle || 'География услуг',
          description: info.regionText || defaultCards[0].description,
          features: (info.regionList || []).filter(
            (item): item is string => typeof item === 'string' && item.trim().length > 0,
          ),
          footer: info.regionFooter || 'Цифровой горизонт Адыгея',
          icon: MapPin,
          gradient: 'from-blue-600 via-cyan-500 to-teal-400',
          accentRgb: '34, 211, 238',
        },
        {
          id: 'projects',
          number: '02',
          title: info.competenciesTitle || defaultCards[1].title,
          subtitle: info.competenciesSubtitle || 'Компетенции',
          description: info.competenciesText || defaultCards[1].description,
          features: (info.competenciesList || []).filter(
            (item): item is string => typeof item === 'string' && item.trim().length > 0,
          ),
          footer: info.competenciesFooter || 'Цифровой горизонт Адыгея',
          icon: Layers,
          gradient: 'from-purple-600 via-pink-500 to-rose-400',
          accentRgb: '244, 114, 182',
        },
      ]
    : defaultCards

  return (
    <section className="relative overflow-hidden py-12 sm:py-16 md:py-20">
      <div className="home-container relative z-10">
        <div className="grid auto-rows-fr grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 lg:gap-8">
          {dynamicCards.map((card, index) => (
            <Card key={card.id} card={card} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}
